<template>
  <div class="tx-review">
    <!-- ============ 页头 ============ -->
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">{{ $t("txReview.menuTitle") }}</h1>
        <div class="vtm-page-head__sub">{{ $t("txReview.subtitle") }}</div>
      </div>
    </div>

    <!-- KPI 概览 -->
    <section class="tx-kpis">
      <div class="tx-kpi">
        <div class="tx-kpi__icon tx-kpi__icon--brand"><i class="el-icon-tickets"></i></div>
        <div class="tx-kpi__body">
          <span class="tx-kpi__label">{{ $t("txReview.kpiTotal") }}</span>
          <strong class="tx-kpi__value">{{ page.totalSize || 0 }}</strong>
          <span class="tx-kpi__hint">{{ $t("txReview.kpiTotalHint") }}</span>
        </div>
      </div>
      <div class="tx-kpi">
        <div class="tx-kpi__icon tx-kpi__icon--success"><i class="el-icon-wallet"></i></div>
        <div class="tx-kpi__body">
          <span class="tx-kpi__label">{{ $t("txReview.kpiRevenue") }}</span>
          <strong class="tx-kpi__value">{{ monthRevenue }}</strong>
          <span class="tx-kpi__hint">{{ $t("txReview.kpiRevenueHint") }}</span>
        </div>
      </div>
      <div
        class="tx-kpi tx-kpi--clickable"
        :class="{ 'tx-kpi--alert': reviewCount > 0 }"
        @click="goToReview"
      >
        <div class="tx-kpi__icon tx-kpi__icon--warning"><i class="el-icon-bell"></i></div>
        <div class="tx-kpi__body">
          <span class="tx-kpi__label">{{ $t("txReview.kpiNeedsReview") }}</span>
          <strong class="tx-kpi__value">{{ reviewCount }}</strong>
          <span class="tx-kpi__hint">
            {{ reviewCount > 0 ? $t("txReview.kpiNeedsReviewHint") : $t("txReview.kpiNeedsReviewClear") }}
            <i v-if="reviewCount > 0" class="el-icon-right"></i>
          </span>
        </div>
      </div>
      <div class="tx-kpi" :class="{ 'tx-kpi--danger': recognitionIssueCount > 0 }">
        <div class="tx-kpi__icon tx-kpi__icon--danger"><i class="el-icon-warning-outline"></i></div>
        <div class="tx-kpi__body">
          <span class="tx-kpi__label">{{ $t("txReview.kpiIssues") }}</span>
          <strong class="tx-kpi__value">{{ recognitionIssueCount }}</strong>
          <span class="tx-kpi__hint">{{ $t("txReview.kpiIssuesHint") }}</span>
        </div>
      </div>
    </section>

    <!-- 控制条：模式 / 筛选 / 排序 / 视图 —— 单行 -->
    <div class="tx-controls radius tx-controls--bar">
      <el-radio-group v-model="mode" size="small" class="tx-seg" @change="handleModeChange">
        <el-radio-button label="purchases">
          <i class="el-icon-goods"></i> {{ $t("txReview.modePurchases") }}
        </el-radio-button>
        <el-radio-button label="review">
          <i class="el-icon-warning-outline"></i> {{ $t("txReview.modeReview") }}
          <span v-if="reviewCount" class="tx-seg__count">{{ reviewCount }}</span>
        </el-radio-button>
        <el-radio-button label="refunds">
          <i class="el-icon-bank-card"></i> {{ $t("txReview.modeRefunds") }}
        </el-radio-button>
      </el-radio-group>

      <vtm-filter-bar
        v-if="mode !== 'refunds'"
        ref="fb"
        class="tx-filterbar"
        :fields="filterFields"
        :search="search"
        :presets="filterPresets"
        :extra-keys="['payState', 'payType', 'sort', 'order']"
        :date-time="dateTime"
        storage-key="tx-review"
        @submit="applySearch"
        @reset="resetSearch"
      >
        <template #lead>
          <el-button size="small" icon="el-icon-printer" :loading="exportLoading" @click="exportOrders">
            {{ $t("common.export") }}
          </el-button>
          <el-date-picker
            v-model="dateTime"
            type="datetimerange"
            size="small"
            class="tx-filter tx-filter--date"
            :picker-options="pickerOptions"
            :default-time="['00:00:00', '23:59:59']"
            range-separator="→"
            :start-placeholder="$t('searchOrder.start')"
            :end-placeholder="$t('searchOrder.end')"
            @change="applySearch"
          ></el-date-picker>
        </template>
        <template #filters>
          <el-select
            v-model="search.payState"
            size="small"
            clearable
            class="tx-filter tx-filter--status"
            :no-data-text="$t('common.noData')"
            :placeholder="$t('searchOrder.payState')"
            @change="applySearch"
          >
            <el-option v-for="item in payStateList" :key="item.id" :label="item.label" :value="item.value"></el-option>
          </el-select>
          <el-select
            v-model="search.payType"
            size="small"
            clearable
            class="tx-filter tx-filter--paytype"
            :no-data-text="$t('common.noData')"
            :placeholder="$t('order.payType')"
            @change="applySearch"
          >
            <el-option v-for="item in payTypeList" :key="item.id" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </template>
      </vtm-filter-bar>

      <div v-if="mode !== 'refunds'" class="tx-controls__view">
        <el-select v-model="sortKey" size="small" class="tx-sort" @change="applySort">
          <el-option v-for="opt in sortOptions" :key="opt.value" :label="opt.label" :value="opt.value"></el-option>
        </el-select>
        <div class="tx-viewtoggle">
          <button type="button" :class="{ 'is-active': viewMode === 'card' }" :title="$t('txReview.viewCard')" @click="viewMode = 'card'">
            <i class="el-icon-menu"></i>
          </button>
          <button type="button" :class="{ 'is-active': viewMode === 'list' }" :title="$t('txReview.viewList')" @click="viewMode = 'list'">
            <i class="el-icon-s-unfold"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ===================== 全部交易 ===================== -->
    <div
      v-if="mode === 'purchases'"
      v-loading="loading"
      :element-loading-text="$t('common.loading')"
      class="tx-panel"
    >
      <!-- 卡片视图 -->
      <div v-if="records.length && viewMode === 'card'" class="tx-grid">
        <article v-for="order in records" :key="order.id" class="tx-card" @click="openDetail(order)">
          <header class="tx-card__head">
            <div class="tx-card__machine">
              <i class="el-icon-cpu"></i>
              <div class="tx-card__machine-text">
                <span class="tx-card__machine-name" :title="order.equipmentName || order.equipmentNo">
                  {{ order.equipmentName || order.equipmentNo || "-" }}
                </span>
                <span v-if="order.equipmentName && order.equipmentNo" class="tx-card__machine-no">{{ order.equipmentNo }}</span>
              </div>
            </div>
            <el-tag
              v-if="needsReview(order) || isRecognitionFailed(order.doorVerifyStatus)"
              size="mini"
              type="danger"
              effect="dark"
              class="tx-card__flag"
            >
              <i class="el-icon-warning"></i> {{ $t("txReview.flagIssue") }}
            </el-tag>
          </header>

          <div class="tx-card__products">
            <div class="tx-card__products-label">
              <span>{{ $t("txReview.productsTaken") }}</span>
              <span class="tx-card__products-count">{{ productSummary(order).totalTaken }}</span>
            </div>
            <div class="tx-products">
              <div
                v-for="(item, index) in (order.ordersGoods || [])"
                :key="item.id || index"
                class="tx-product"
                :class="{ 'tx-product--mismatch': isQtyMismatch(item) }"
              >
                <div class="tx-product__thumb">
                  <el-image v-if="goodsImage(item)" :src="goodsImage(item)" fit="contain">
                    <div slot="error" class="tx-product__thumb-ph"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                  <div v-else class="tx-product__thumb-ph"><i class="el-icon-picture-outline"></i></div>
                  <span class="tx-product__qty">×{{ takenQty(item) }}</span>
                </div>
                <div class="tx-product__name" :title="item.goodsName">{{ item.goodsName || "-" }}</div>
                <div v-if="isQtyMismatch(item)" class="tx-product__flag">
                  <i class="el-icon-warning"></i> {{ item.successNum || 0 }}/{{ item.buyNum || 0 }}
                </div>
              </div>
              <el-empty
                v-if="!(order.ordersGoods && order.ordersGoods.length)"
                :description="$t('txReview.noProducts')"
                :image-size="52"
              ></el-empty>
            </div>
          </div>

          <footer class="tx-card__foot">
            <div class="tx-card__amount">
              <span class="tx-card__amount-label">{{ $t("txReview.colAmount") }}</span>
              <strong>{{ formatMoney(order.payablePrice) }}</strong>
            </div>
            <div class="tx-card__status">
              <el-tag size="mini" effect="dark" :type="payStateTagType(order.payState)">{{ payStateLabel(order) }}</el-tag>
              <el-tag size="mini" effect="plain" :type="doorVerifyStatusTagType(order.doorVerifyStatus)">
                {{ doorVerifyLabel(order.doorVerifyStatus) }}
              </el-tag>
            </div>
            <el-button
              v-if="needsReconcile(order)"
              type="warning"
              size="mini"
              class="tx-card__review-btn"
              @click.stop="openReview(order)"
            >
              {{ $t("txReview.reviewNow") }}
            </el-button>
          </footer>
          <div class="tx-card__time"><i class="el-icon-time"></i> {{ order.createTime || "-" }} · {{ order.orderNumber }}</div>
        </article>
      </div>

      <!-- 列表视图 -->
      <div v-else-if="records.length" class="tx-list">
        <div class="tx-list__head">
          <span>{{ $t("txReview.colMachine") }}</span>
          <span>{{ $t("txReview.colOrder") }}</span>
          <span>{{ $t("txReview.colProducts") }}</span>
          <span>{{ $t("txReview.colStatus") }}</span>
          <span class="tx-list__num">{{ $t("txReview.colAmount") }}</span>
          <span></span>
        </div>
        <div v-for="order in records" :key="order.id" class="tx-row" @click="openDetail(order)">
          <div class="tx-row__machine">
            <i class="el-icon-cpu"></i>
            <div class="tx-row__machine-text">
              <span class="tx-row__strong" :title="order.equipmentName || order.equipmentNo">
                {{ order.equipmentName || order.equipmentNo || "-" }}
              </span>
              <span v-if="order.equipmentName && order.equipmentNo" class="tx-row__sub">{{ order.equipmentNo }}</span>
            </div>
          </div>
          <div class="tx-row__order">
            <span class="tx-row__mono">{{ order.orderNumber || "-" }}</span>
            <span class="tx-row__sub">{{ order.createTime || "-" }}</span>
          </div>
          <div class="tx-row__products">
            <div class="tx-thumbs">
              <div
                v-for="(item, index) in (order.ordersGoods || []).slice(0, 4)"
                :key="item.id || index"
                class="tx-thumbs__img"
                :title="item.goodsName"
              >
                <el-image v-if="goodsImage(item)" :src="goodsImage(item)" fit="contain">
                  <div slot="error" class="tx-product__thumb-ph"><i class="el-icon-picture-outline"></i></div>
                </el-image>
                <div v-else class="tx-product__thumb-ph"><i class="el-icon-picture-outline"></i></div>
              </div>
              <span v-if="(order.ordersGoods || []).length > 4" class="tx-thumbs__more">
                +{{ order.ordersGoods.length - 4 }}
              </span>
              <span v-if="!(order.ordersGoods && order.ordersGoods.length)" class="tx-row__sub">{{ $t("txReview.noProducts") }}</span>
            </div>
            <span class="tx-row__sub">{{ $t("txReview.itemsTaken", { n: productSummary(order).totalTaken }) }}</span>
          </div>
          <div class="tx-row__status">
            <el-tag size="mini" effect="dark" :type="payStateTagType(order.payState)">{{ payStateLabel(order) }}</el-tag>
            <el-tag size="mini" effect="plain" :type="doorVerifyStatusTagType(order.doorVerifyStatus)">
              {{ doorVerifyLabel(order.doorVerifyStatus) }}
            </el-tag>
          </div>
          <div class="tx-row__amount tx-list__num">{{ formatMoney(order.payablePrice) }}</div>
          <div class="tx-row__action">
            <el-button
              v-if="needsReconcile(order)"
              type="warning"
              size="mini"
              @click.stop="openReview(order)"
            >
              {{ $t("txReview.reviewNow") }}
            </el-button>
            <i v-else class="el-icon-arrow-right tx-row__chevron"></i>
          </div>
        </div>
      </div>

      <el-empty v-else :description="$t('common.noData')"></el-empty>

      <el-pagination
        v-if="records.length"
        background
        class="tx-pagination"
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="page.pageIndex"
        :total="page.totalSize"
        :page-size="page.pageSize"
        :page-sizes="[12, 24, 48, 96]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>

    <!-- ===================== 待处理 ===================== -->
    <div v-else-if="mode === 'review'" v-loading="reviewLoading" :element-loading-text="$t('common.loading')" class="tx-panel">
      <!-- 卡片（媒体优先）视图 -->
      <div v-if="reviewPageItems.length && viewMode === 'card'" class="tx-review-list">
        <article v-for="order in reviewPageItems" :key="order.id" class="review-card">
          <div class="review-card__media">
            <div class="review-card__media-head">
              <i class="el-icon-video-camera"></i>
              <span>{{ $t("txReview.evidence") }}</span>
              <el-tag size="mini" type="info">{{ (videoCache[order.id] || []).length }}</el-tag>
            </div>
            <div v-loading="videoLoadingMap[order.id]" class="review-card__videos">
              <div
                v-for="(video, vIndex) in (videoCache[order.id] || [])"
                :key="video.id || vIndex"
                class="review-card__video"
                @click="previewVideo(video)"
              >
                <video :src="video.resourceUrl" muted playsinline preload="metadata"></video>
                <div class="review-card__video-play"><i class="el-icon-video-play"></i></div>
              </div>
              <el-empty
                v-if="!videoLoadingMap[order.id] && !(videoCache[order.id] && videoCache[order.id].length)"
                :description="$t('txReview.noVideo')"
                :image-size="48"
              ></el-empty>
            </div>
          </div>

          <div class="review-card__body">
            <div class="review-issue" :class="issueSeverityClass(order)">
              <i :class="doorVerifyStatusTagIcon(order.doorVerifyStatus)"></i>
              <div class="review-issue__text">
                <strong>{{ doorVerifyLabel(order.doorVerifyStatus) }}</strong>
                <span>{{ issueDetail(order) }}</span>
              </div>
            </div>

            <div class="review-card__meta">
              <div class="review-card__meta-item">
                <span>{{ $t("txReview.colMachine") }}</span>
                <strong>{{ order.equipmentName || order.equipmentNo || "-" }}</strong>
              </div>
              <div class="review-card__meta-item">
                <span>{{ $t("order.orderNumber") }}</span>
                <strong class="tx-row__mono">{{ order.orderNumber || "-" }}</strong>
              </div>
              <div class="review-card__meta-item">
                <span>{{ $t("order.openDoorTime") }}</span>
                <strong>{{ order.openDoorTime || order.createTime || "-" }}</strong>
              </div>
              <div class="review-card__meta-item">
                <span>{{ $t("txReview.colAmount") }}</span>
                <strong>{{ formatMoney(order.payablePrice) }}</strong>
              </div>
            </div>

            <div class="review-card__products">
              <span class="review-card__products-label">{{ $t("txReview.claimedProducts") }}</span>
              <div class="review-card__chips">
                <span v-for="(item, index) in (order.ordersGoods || [])" :key="item.id || index" class="review-chip">
                  <span class="review-chip__name">{{ item.goodsName || "-" }}</span>
                  <span class="review-chip__qty">×{{ takenQty(item) }}</span>
                </span>
                <span v-if="!(order.ordersGoods && order.ordersGoods.length)" class="review-card__none">
                  {{ $t("txReview.noProducts") }}
                </span>
              </div>
            </div>

            <div class="review-card__actions">
              <el-button size="small" icon="el-icon-view" @click="openDetail(order)">
                {{ $t("txReview.viewDetail") }}
              </el-button>
              <el-button type="primary" size="small" icon="el-icon-s-check" @click="openReview(order)">
                {{ $t("txReview.reviewNow") }}
              </el-button>
            </div>
          </div>
        </article>
      </div>

      <!-- 列表视图 -->
      <div v-else-if="reviewPageItems.length" class="tx-list tx-list--review">
        <div class="tx-list__head">
          <span>{{ $t("txReview.colMachine") }}</span>
          <span>{{ $t("txReview.colIssue") }}</span>
          <span>{{ $t("txReview.colProducts") }}</span>
          <span class="tx-list__num">{{ $t("txReview.colAmount") }}</span>
          <span></span>
        </div>
        <div v-for="order in reviewPageItems" :key="order.id" class="tx-row tx-row--review" @click="openDetail(order)">
          <div class="tx-row__machine">
            <i class="el-icon-cpu"></i>
            <div class="tx-row__machine-text">
              <span class="tx-row__strong">{{ order.equipmentName || order.equipmentNo || "-" }}</span>
              <span class="tx-row__sub tx-row__mono">{{ order.orderNumber || "-" }}</span>
            </div>
          </div>
          <div class="tx-row__issue">
            <span class="tx-issue-chip" :class="issueSeverityClass(order)">
              <i :class="doorVerifyStatusTagIcon(order.doorVerifyStatus)"></i>
              {{ doorVerifyLabel(order.doorVerifyStatus) }}
            </span>
            <span class="tx-row__sub">
              <i class="el-icon-video-camera-solid"></i> {{ (videoCache[order.id] || []).length }} · {{ issueDetail(order) }}
            </span>
          </div>
          <div class="tx-row__products">
            <div class="review-card__chips">
              <span v-for="(item, index) in (order.ordersGoods || [])" :key="item.id || index" class="review-chip">
                <span class="review-chip__name">{{ item.goodsName || "-" }}</span>
                <span class="review-chip__qty">×{{ takenQty(item) }}</span>
              </span>
              <span v-if="!(order.ordersGoods && order.ordersGoods.length)" class="tx-row__sub">{{ $t("txReview.noProducts") }}</span>
            </div>
          </div>
          <div class="tx-row__amount tx-list__num">{{ formatMoney(order.payablePrice) }}</div>
          <div class="tx-row__action">
            <el-button type="primary" size="mini" icon="el-icon-s-check" @click.stop="openReview(order)">
              {{ $t("txReview.reviewNow") }}
            </el-button>
          </div>
        </div>
      </div>

      <el-empty v-else :description="$t('txReview.emptyReview')"></el-empty>

      <el-pagination
        v-if="reviewSource.length > reviewPage.pageSize"
        background
        class="tx-pagination"
        layout="total, prev, pager, next"
        :current-page="reviewPage.pageIndex"
        :total="reviewSource.length"
        :page-size="reviewPage.pageSize"
        @current-change="handleReviewPageChange"
      ></el-pagination>
    </div>

    <!-- ===================== 退款台账 ===================== -->
    <div v-else-if="mode === 'refunds'" class="tx-panel tx-panel--refunds">
      <order-refund></order-refund>
    </div>

    <!-- 交易详情抽屉 -->
    <el-drawer
      :visible.sync="detailDrawerVisible"
      :with-header="false"
      size="620px"
      :append-to-body="true"
      @closed="handleDetailClosed"
    >
      <div class="detail">
        <div class="detail__topbar">
          <div class="detail__topbar-title">
            <i class="el-icon-data-analysis"></i>
            <span>{{ $t("txReview.detailTitle") }}</span>
          </div>
          <el-button type="text" icon="el-icon-close" @click="detailDrawerVisible = false"></el-button>
        </div>

        <div class="detail__scroll">
          <div class="detail__hero">
            <div class="detail__hero-machine">
              <i class="el-icon-cpu"></i>
              <span>{{ detailOrder.equipmentName || detailOrder.equipmentNo || "-" }}</span>
            </div>
            <div class="detail__order">{{ detailOrder.orderNumber || "-" }}</div>
            <div class="detail__tags">
              <el-tag effect="dark" size="small" :type="payStateTagType(detailOrder.payState)">
                {{ payStateLabel(detailOrder) }}
              </el-tag>
              <el-tag effect="plain" size="small" :type="doorVerifyStatusTagType(detailOrder.doorVerifyStatus)">
                <i :class="doorVerifyStatusTagIcon(detailOrder.doorVerifyStatus)"></i>
                {{ doorVerifyLabel(detailOrder.doorVerifyStatus) }}
              </el-tag>
              <el-tag v-if="Number(detailOrder.refundPrice) > 0" effect="dark" size="small" type="danger">
                {{ $t("refund.refunded") }}
              </el-tag>
            </div>
            <div class="detail__stats">
              <div class="detail__stat">
                <span>{{ $t("txReview.colAmount") }}</span>
                <strong>{{ formatMoney(detailOrder.payablePrice) }}</strong>
              </div>
              <div class="detail__stat">
                <span>{{ $t("order.authAmount") }}</span>
                <strong>{{ formatMoney(detailOrder.authAmount) }}</strong>
              </div>
              <div class="detail__stat">
                <span>{{ $t("order.detectType") }}</span>
                <strong>{{ detectTypeLabel(detailOrder.detectType) }}</strong>
              </div>
            </div>
          </div>

          <!-- 需对账时：内联对账面板（判定 / 证据 / 识别vs计费 / 重算 / 结论），取代只读的商品与证据区 -->
          <reconcile-panel
            v-if="needsReconcile(detailOrder)"
            ref="reconcilePanel"
            class="detail__reconcile"
            :order-info="detailOrder"
            :approval-status-options="approvalStatusOptions"
            :show-header="false"
            @loading="v => (reconcileSubmitting = v)"
            @refresh="handleReconcileRefresh"
          ></reconcile-panel>

          <section v-if="!needsReconcile(detailOrder)" class="detail__section">
            <div class="detail__section-head">
              <span><i class="el-icon-goods"></i> {{ $t("txReview.productsTaken") }}</span>
              <el-tag size="mini" type="info">{{ (detailOrder.ordersGoods || []).length }}</el-tag>
            </div>
            <div v-if="(detailOrder.ordersGoods || []).length" class="detail__goods">
              <div v-for="(item, index) in detailOrder.ordersGoods" :key="item.id || index" class="detail-goods">
                <div class="detail-goods__thumb">
                  <el-image v-if="goodsImage(item)" :src="goodsImage(item)" fit="contain">
                    <div slot="error" class="tx-product__thumb-ph"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                  <div v-else class="tx-product__thumb-ph"><i class="el-icon-picture-outline"></i></div>
                </div>
                <div class="detail-goods__info">
                  <div class="detail-goods__name">{{ item.goodsName || "-" }}</div>
                  <div class="detail-goods__metrics">
                    <span>{{ $t("order.buyNum") }} <strong>{{ item.buyNum || 0 }}</strong></span>
                    <span :class="{ 'is-mismatch': isQtyMismatch(item) }">
                      {{ $t("order.successNum") }} <strong>{{ item.successNum || 0 }}</strong>
                    </span>
                    <span>{{ $t("txReview.colAmount") }} <strong>{{ formatMoney(item.payablePrice) }}</strong></span>
                  </div>
                </div>
              </div>
            </div>
            <el-empty v-else :description="$t('txReview.noProducts')" :image-size="52"></el-empty>
          </section>

          <section v-if="!needsReconcile(detailOrder)" class="detail__section">
            <div class="detail__section-head">
              <span><i class="el-icon-video-camera"></i> {{ $t("txReview.evidence") }}</span>
              <el-tag size="mini" type="info">{{ detailVideos.length }}</el-tag>
            </div>
            <div v-loading="detailVideoLoading">
              <div v-if="detailVideos.length" class="detail__videos">
                <video
                  v-for="(video, index) in detailVideos"
                  :key="video.id || index"
                  class="detail__video"
                  :src="video.resourceUrl"
                  controls
                  preload="metadata"
                ></video>
              </div>
              <el-empty v-else :description="$t('txReview.noVideo')" :image-size="52"></el-empty>
            </div>
          </section>

          <section class="detail__section">
            <div class="detail__section-head">
              <span><i class="el-icon-unlock"></i> {{ $t("txReview.recognitionSection") }}</span>
            </div>
            <div class="detail__kv-grid">
              <div v-for="field in recognitionFields" :key="field.label" class="detail__kv">
                <span class="detail__kv-label">{{ field.label }}</span>
                <span class="detail__kv-value">{{ field.value }}</span>
              </div>
            </div>
          </section>

          <section class="detail__section">
            <div class="detail__section-head">
              <span><i class="el-icon-wallet"></i> {{ $t("order.paymentInfo") }}</span>
            </div>
            <div class="detail__kv-grid">
              <div v-for="field in paymentFields" :key="field.label" class="detail__kv">
                <span class="detail__kv-label">{{ field.label }}</span>
                <span class="detail__kv-value">{{ field.value }}</span>
              </div>
            </div>
          </section>

          <section class="detail__section">
            <div class="detail__section-head">
              <span><i class="el-icon-time"></i> {{ $t("order.timeline") }}</span>
            </div>
            <div class="detail__timeline">
              <div v-for="node in timelineNodes" :key="node.label" class="detail__timeline-node">
                <span class="detail__timeline-dot"></span>
                <div class="detail__timeline-body">
                  <span class="detail__timeline-label">{{ node.label }}</span>
                  <strong class="detail__timeline-value">{{ node.value }}</strong>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div class="detail__footer">
          <el-button
            v-if="needsReconcile(detailOrder)"
            type="primary"
            :loading="reconcileSubmitting"
            icon="el-icon-s-check"
            @click="submitReconcile"
          >
            {{ $t("order.confirmSettle") }}
          </el-button>
          <el-button v-if="canRefund(detailOrder)" type="danger" plain icon="el-icon-bank-card" @click="openRefund(detailOrder)">
            {{ $t("refund.issue") }}
          </el-button>
          <el-button @click="detailDrawerVisible = false">{{ $t("common.close") }}</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- 退款 -->
    <refund-dialog
      :visible="refundDialogVisible"
      :order="refundOrder"
      @close="refundDialogVisible = false"
      @refresh="handleRefundRefresh"
    ></refund-dialog>

    <!-- 视频预览 -->
    <el-dialog :visible.sync="videoPreviewVisible" :append-to-body="true" width="720px" :title="$t('order.videoPreview')">
      <video v-if="previewResourceUrl" :src="previewResourceUrl" controls width="100%"></video>
    </el-dialog>

  </div>
</template>

<script>
  import ReconcilePanel from "_c/order/ReconcilePanel.vue";
  import RefundDialog from "_c/order/RefundDialog.vue";
  import OrderRefund from "@/views/order/OrderRefund.vue";
  import VtmFilterBar from "@/components/common/VtmFilterBar.vue";
  import orderStatus from "@/utils/orderStatus";
  import { orderCardLabel } from "@/utils/paymentInfo";

  // order/page's ORDER BY is a raw column substitution (see OrdersDao.xml) —
  // it needs the actual `orders` table column, not the camelCase JS field name.
  const SORT_COLUMN_MAP = {
    createTime: "buy_time",
    payablePrice: "payable_price"
  };

  export default {
    name: "TransactionReview",
    components: {
      ReconcilePanel,
      RefundDialog,
      OrderRefund,
      VtmFilterBar
    },
    data() {
      return {
        refundDialogVisible: false,
        refundOrder: {},
        mode: "purchases",
        viewMode: "list",
        sortKey: "createTime_desc",
        loading: false,
        exportLoading: false,
        reviewLoading: false,
        search: {
          orderNumber: "",
          userName: "",
          equipmentNo: "",
          equipmentName: "",
          goodsName: "",
          payState: "",
          payType: "",
          recogId: "",
          startTime: "",
          endTime: "",
          sort: "buy_time",
          order: "desc"
        },
        dateTime: [],
        page: {
          pageIndex: 1,
          pageSize: 12,
          totalSize: 0
        },
        records: [],
        reviewSource: [],
        reviewPage: {
          pageIndex: 1,
          pageSize: 8
        },
        videoCache: {},
        videoLoadingMap: {},
        payStateList: [],
        payTypeList: [],
        approvalStatusOptions: [],
        detailDrawerVisible: false,
        detailOrder: {},
        detailVideos: [],
        detailVideoLoading: false,
        reconcileSubmitting: false,
        videoPreviewVisible: false,
        previewResourceUrl: "",
        daySales: {},
        pickerOptions: {
          shortcuts: [
            {
              text: this.$t("txReview.today"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setHours(0, 0, 0, 0);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("txReview.days7"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("txReview.days30"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                picker.$emit("pick", [start, end]);
              }
            }
          ]
        }
      };
    },
    computed: {
      sortOptions() {
        return [
          { value: "createTime_desc", label: this.$t("txReview.sortNewest") },
          { value: "createTime_asc", label: this.$t("txReview.sortOldest") },
          { value: "payablePrice_desc", label: this.$t("txReview.sortAmountDesc") },
          { value: "payablePrice_asc", label: this.$t("txReview.sortAmountAsc") }
        ];
      },
      filterFields() {
        return [
          { value: "equipmentName", label: this.$t("searchOrder.equipmentName") },
          { value: "orderNumber", label: this.$t("searchOrder.orderNumber") },
          { value: "userName", label: this.$t("searchOrder.userName") },
          { value: "equipmentNo", label: this.$t("searchOrder.equipmentNo") },
          { value: "goodsName", label: this.$t("order.goodsName") },
          { value: "recogId", label: this.$t("searchOrder.recogId") }
        ];
      },
      filterPresets() {
        return [
          {
            id: "deliveredThisMonth",
            name: this.$t("searchOrder.presetDeliveredThisMonth"),
            set: { payState: 3 },
            range: "thisMonth"
          },
          {
            id: "deliveredLastMonth",
            name: this.$t("searchOrder.presetDeliveredLastMonth"),
            set: { payState: 3 },
            range: "lastMonth"
          },
          {
            id: "refunded30",
            name: this.$t("searchOrder.presetRefunded30"),
            set: { payState: -2 },
            range: "last30"
          }
        ];
      },
      monthRevenue() {
        return this.formatMoney(Number(this.daySales.monthSales) || 0);
      },
      reviewCount() {
        return this.reviewSource.filter(order => orderStatus.needsReview(order)).length;
      },
      recognitionIssueCount() {
        return this.reviewSource.filter(order => orderStatus.isRecognitionFailed(order.doorVerifyStatus)).length;
      },
      reviewPageItems() {
        const start = (this.reviewPage.pageIndex - 1) * this.reviewPage.pageSize;
        return this.reviewSource.slice(start, start + this.reviewPage.pageSize);
      },
      detailOpenDoorInfo() {
        return this.detailOrder.ordersOpenDoorDTO || {};
      },
      recognitionFields() {
        const o = this.detailOrder;
        return [
          { label: this.$t("order.detectType"), value: this.detectTypeLabel(o.detectType) },
          { label: this.$t("order.doorVerifyStatus"), value: this.doorVerifyLabel(o.doorVerifyStatus) },
          { label: this.$t("order.sysVerifyWeight"), value: orderStatus.formatSysVerifyWeight(this.$t.bind(this), o.sysVerifyWeight) },
          { label: this.$t("order.userVerifyStatus"), value: orderStatus.formatUserVerifyStatus(this.$t.bind(this), o.userVerifyStatus) },
          { label: this.$t("order.openResultDescription"), value: this.dash(o.openResultDescription || this.detailOpenDoorInfo.openResultDescription) },
          { label: this.$t("order.recogId"), value: this.dash(this.detailOpenDoorInfo.recogId) }
        ];
      },
      paymentFields() {
        const o = this.detailOrder;
        const door = this.detailOpenDoorInfo;
        const fields = [
          { label: this.$t("order.payType"), value: this.payTypeLabel(o.payType) },
          { label: this.$t("order.payState"), value: this.payStateLabel(o) },
          { label: this.$t("order.payPrice"), value: this.formatMoney(o.payPrice) },
          { label: this.$t("order.discountPrice"), value: this.formatMoney(o.discountPrice) },
          { label: this.$t("order.authAmount"), value: this.formatMoney(o.authAmount) },
          { label: this.$t("order.payFinishTime"), value: this.dash(o.payFinishTime || door.payFinishTime) },
          { label: this.$t("order.transactionId"), value: this.dash(o.transactionId || door.transactionId) },
          { label: this.$t("order.sessionId"), value: this.dash(o.sessionId || door.sessionId) }
        ];
        const card = this.cardLabelOf(o);
        if (card) {
          fields.splice(2, 0, { label: this.$t("refund.card"), value: card });
        }
        if (Number(o.refundPrice) > 0) {
          fields.push({ label: this.$t("order.refundPrice"), value: this.formatMoney(o.refundPrice) });
          fields.push({ label: this.$t("order.refundTime"), value: this.dash(o.refundTime) });
        }
        return fields;
      },
      timelineNodes() {
        const o = this.detailOrder;
        return [
          { label: this.$t("order.createTime"), value: this.dash(o.createTime) },
          { label: this.$t("order.openDoorTime"), value: this.dash(o.openDoorTime) },
          { label: this.$t("order.closeDoorTime"), value: this.dash(o.closeDoorTime) },
          { label: this.$t("order.payFinishTime"), value: this.dash(o.payFinishTime) }
        ];
      }
    },
    mounted() {
      this.listPayState();
      this.listPayType();
      this.listApprovalStatus();
      this.fetchPurchases(1);
      this.fetchReviewQueue();
      this.getMonthRevenue();
    },
    methods: {
      getMonthRevenue() {
        this.$api.request(this.$url.Consoles.getSales, this.$method.get, {}).then(res => {
          if (res.code === 0) {
            this.daySales = res.data || {};
          }
        }).catch(() => {});
      },
      // ---- 状态格式化（复用 orderStatus 工具） ----
      payStateTagType: orderStatus.payStateTagType,
      doorVerifyStatusTagType: orderStatus.doorVerifyStatusTagType,
      doorVerifyStatusTagIcon: orderStatus.doorVerifyStatusTagIcon,
      needsReview: orderStatus.needsReview,
      needsReconcile: orderStatus.needsReconcile,
      isRecognitionFailed: orderStatus.isRecognitionFailed,
      doorVerifyLabel(status) {
        return orderStatus.formatDoorVerifyStatus(this.$t.bind(this), status);
      },
      detectTypeLabel(type) {
        return orderStatus.formatDetectType(this.$t.bind(this), type);
      },
      payStateLabel(order) {
        return this.$cloud.getDictLabel(this.payStateList, order.payState) || "-";
      },
      payTypeLabel(value) {
        return this.$cloud.getDictLabel(this.payTypeList, value) || "-";
      },
      dash(value) {
        return value === null || value === undefined || value === "" ? "-" : value;
      },
      formatMoney(value) {
        if (value === null || value === undefined || value === "") {
          return "-";
        }
        const num = Number(value);
        if (Number.isNaN(num)) {
          return String(value);
        }
        return `$${num.toFixed(2)}`;
      },
      goodsImage(item) {
        return (item && (item.goodsUrl || item.url)) || this.detailOrder.goodsUrl || "";
      },
      takenQty(item) {
        if (item.successNum !== null && item.successNum !== undefined && item.successNum !== "") {
          return item.successNum;
        }
        return item.buyNum || 0;
      },
      isQtyMismatch(item) {
        if (item.successNum === null || item.successNum === undefined || item.successNum === "") {
          return false;
        }
        return Number(item.successNum) !== Number(item.buyNum);
      },
      productSummary(order) {
        const goods = order.ordersGoods || [];
        const totalTaken = goods.reduce((sum, item) => sum + (Number(this.takenQty(item)) || 0), 0);
        return { totalTaken };
      },
      // ---- 支付/退款 ----
      cardLabelOf(order) {
        return orderCardLabel(order);
      },
      canRefund(order) {
        if (!order || !order.id) {
          return false;
        }
        const charged = Number(order.payablePrice) || 0;
        const refunded = Number(order.refundPrice) || 0;
        return charged > 0 && refunded < charged;
      },
      openRefund(order) {
        this.refundOrder = { ...order };
        this.refundDialogVisible = true;
      },
      handleRefundRefresh() {
        this.refundDialogVisible = false;
        this.detailDrawerVisible = false;
        this.fetchPurchases(this.page.pageIndex);
        this.fetchReviewQueue();
      },
      issueSeverityClass(order) {
        if (orderStatus.isRecognitionFailed(order.doorVerifyStatus)) {
          return "is-danger";
        }
        return "is-warning";
      },
      issueDetail(order) {
        const parts = [];
        const openDoor = order.ordersOpenDoorDTO || {};
        const desc = order.openResultDescription || openDoor.openResultDescription;
        if (desc) {
          parts.push(desc);
        }
        if (Number(order.userVerifyStatus) === 2) {
          parts.push(orderStatus.formatUserVerifyStatus(this.$t.bind(this), order.userVerifyStatus));
        }
        return parts.length ? parts.join(" · ") : this.$t("txReview.issueGeneric");
      },

      // ---- 字典 ----
      listPayState() {
        this.$api.dict("gp_pay_state").then(res => {
          if (res.code === 0) {
            this.payStateList = res.data;
          }
        });
      },
      listPayType() {
        this.$api.dict("gp_pay_type").then(res => {
          if (res.code === 0) {
            this.payTypeList = res.data;
          }
        });
      },
      listApprovalStatus() {
        this.$api.dict("approval_status_list").then(res => {
          if (res.code === 0) {
            this.approvalStatusOptions = Array.isArray(res.data)
              ? res.data.map(item => ({
                  value: Number(item.value ?? item.dictValue),
                  label: item.label || item.dictLabel
                }))
              : [];
          }
        });
      },

      normalizeOrder(order) {
        const openDoorInfo = order.ordersOpenDoorDTO || {};
        const fallbackFields = [
          "openDoorTime",
          "closeDoorTime",
          "detectType",
          "doorVerifyStatus",
          "sysVerifyWeight",
          "userVerifyStatus",
          "openResultDescription",
          "authAmount",
          "payFinishTime"
        ];
        return fallbackFields.reduce((result, field) => {
          if (result[field] === null || result[field] === undefined || result[field] === "") {
            result[field] = openDoorInfo[field];
          }
          return result;
        }, { ...order });
      },

      buildQuery(extra) {
        return this.$cloud.objMerge({ ...this.search }, extra);
      },

      exportOrders() {
        this.$confirm(this.$t("searchOrder.export"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.exportLoading = true;
            this.$api.download(this.$url.Order.export, this.buildQuery({})).finally(() => {
              this.exportLoading = false;
            });
          })
          .catch(err => {
            if (err && err.msg) this.$message.error(err.msg);
          });
      },

      fetchPurchases(pageIndex) {
        this.loading = true;
        this.page.pageIndex = pageIndex || 1;
        const params = this.buildQuery({ pageIndex: this.page.pageIndex, pageSize: this.page.pageSize });
        this.$api
          .request(this.$url.Order.page, this.$method.get, params)
          .then(res => {
            if (res.code === 0) {
              this.records = (res.data.records || []).map(item => this.normalizeOrder(item));
              this.page.totalSize = parseInt(res.data.total) || 0;
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      },

      fetchReviewQueue() {
        this.reviewLoading = true;
        const params = this.buildQuery({ pageIndex: 1, pageSize: 200 });
        this.$api
          .request(this.$url.Order.page, this.$method.get, params)
          .then(res => {
            if (res.code === 0) {
              const all = (res.data.records || []).map(item => this.normalizeOrder(item));
              this.reviewSource = all.filter(
                order =>
                  orderStatus.needsReview(order) || orderStatus.isRecognitionFailed(order.doorVerifyStatus)
              );
              this.sortReviewSource();
              this.reviewPage.pageIndex = 1;
              this.loadVideosForVisibleReview();
            }
            this.reviewLoading = false;
          })
          .catch(() => {
            this.reviewLoading = false;
          });
      },

      loadVideosForVisibleReview() {
        this.reviewPageItems.forEach(order => this.loadVideos(order.id));
      },

      loadVideos(ordersId) {
        if (!ordersId || this.videoCache[ordersId]) {
          return;
        }
        this.$set(this.videoLoadingMap, ordersId, true);
        this.$api
          .request(this.$url.OpenDoor.getOrderVideoResources, this.$method.get, { ordersId })
          .then(res => {
            this.$set(this.videoCache, ordersId, res.code === 0 && Array.isArray(res.data) ? res.data : []);
            this.$set(this.videoLoadingMap, ordersId, false);
          })
          .catch(() => {
            this.$set(this.videoCache, ordersId, []);
            this.$set(this.videoLoadingMap, ordersId, false);
          });
      },

      openDetail(order) {
        this.detailOrder = this.normalizeOrder({ ...order });
        this.detailDrawerVisible = true;
        this.loadDetailVideos(order.id);
      },
      loadDetailVideos(ordersId) {
        this.detailVideos = [];
        if (!ordersId) {
          return;
        }
        if (this.videoCache[ordersId]) {
          this.detailVideos = this.videoCache[ordersId];
          return;
        }
        this.detailVideoLoading = true;
        this.$api
          .request(this.$url.OpenDoor.getOrderVideoResources, this.$method.get, { ordersId })
          .then(res => {
            const videos = res.code === 0 && Array.isArray(res.data) ? res.data : [];
            this.detailVideos = videos;
            this.$set(this.videoCache, ordersId, videos);
            this.detailVideoLoading = false;
          })
          .catch(() => {
            this.detailVideoLoading = false;
          });
      },
      handleDetailClosed() {
        this.detailOrder = {};
        this.detailVideos = [];
      },

      // 复核入口现在统一进入详情抽屉——需对账的订单会内联展示对账面板
      openReview(order) {
        this.openDetail(order);
      },
      submitReconcile() {
        if (this.$refs.reconcilePanel) {
          this.$refs.reconcilePanel.submit();
        }
      },
      handleReconcileRefresh() {
        this.detailDrawerVisible = false;
        this.videoCache = {};
        this.fetchPurchases(this.page.pageIndex);
        this.fetchReviewQueue();
      },
      previewVideo(video) {
        this.previewResourceUrl = video.resourceUrl || "";
        this.videoPreviewVisible = true;
      },

      // ---- 搜索 / 排序 / 分页 / 模式 / 视图 ----
      applySearch() {
        if (this.dateTime && this.dateTime.length > 0) {
          // datetimerange 选择器本身精确到秒（未手动调整时间时以 default-time 00:00:00/23:59:59 为准），
          // 直接透传选中的时刻，不再强制改写到当天末尾
          this.search.startTime = this.$timeUtil.formatTime(new Date(this.dateTime[0]));
          this.search.endTime = this.$timeUtil.formatTime(new Date(this.dateTime[1]));
        } else {
          this.search.startTime = "";
          this.search.endTime = "";
        }
        this.fetchPurchases(1);
        this.fetchReviewQueue();
      },
      resetSearch() {
        this.search.orderNumber = "";
        this.search.equipmentNo = "";
        this.search.equipmentName = "";
        this.search.userName = "";
        this.search.goodsName = "";
        this.search.payState = "";
        this.search.payType = "";
        this.search.recogId = "";
        this.dateTime = [];
        this.applySearch();
      },
      applySort() {
        const idx = this.sortKey.lastIndexOf("_");
        const field = this.sortKey.slice(0, idx);
        this.search.sort = SORT_COLUMN_MAP[field] || field;
        this.search.order = this.sortKey.slice(idx + 1);
        this.fetchPurchases(1);
        this.sortReviewSource();
      },
      sortReviewSource() {
        const idx = this.sortKey.lastIndexOf("_");
        const field = this.sortKey.slice(0, idx);
        const dir = this.sortKey.slice(idx + 1);
        const mult = dir === "asc" ? 1 : -1;
        const valueOf = order =>
          field === "payablePrice" ? Number(order.payablePrice) || 0 : order.createTime || "";
        this.reviewSource = [...this.reviewSource].sort((a, b) => {
          const av = valueOf(a);
          const bv = valueOf(b);
          if (av < bv) {
            return -1 * mult;
          }
          if (av > bv) {
            return 1 * mult;
          }
          return 0;
        });
      },
      handleSizeChange(pageSize) {
        this.page.pageSize = pageSize;
        this.fetchPurchases(1);
      },
      handleCurrentChange(pageIndex) {
        this.fetchPurchases(pageIndex);
      },
      handleReviewPageChange(pageIndex) {
        this.reviewPage.pageIndex = pageIndex;
        this.loadVideosForVisibleReview();
      },
      handleModeChange() {
        if (this.mode === "review") {
          this.loadVideosForVisibleReview();
        }
      },
      goToReview() {
        this.mode = "review";
        this.loadVideosForVisibleReview();
      }
    }
  };
</script>

<style scoped lang="scss">
  $border: var(--vtm-line);
  $muted: var(--vtm-ink-3);
  $ink: var(--vtm-ink);
  $brand: var(--vtm-mint-ink);

  .tx-review { padding: 12px; }

  /* KPI */
  .tx-kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 10px;
  }

  .tx-kpi {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: var(--vtm-surface);
    border: 1px solid $border;
    border-radius: 10px;
    transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  }

  .tx-kpi--clickable { cursor: pointer; }
  .tx-kpi--clickable:hover { box-shadow: 0 6px 18px rgba(31, 45, 61, 0.1); transform: translateY(-1px); }
  .tx-kpi--alert { border-color: var(--vtm-warn-soft); background: var(--vtm-warn-soft); }
  .tx-kpi--danger { border-color: var(--vtm-crit-soft); background: var(--vtm-crit-soft); }

  .tx-kpi__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 10px;
    font-size: 22px;
    flex-shrink: 0;
  }

  .tx-kpi__icon--brand { background: var(--vtm-info-soft); color: var(--vtm-info); }
  .tx-kpi__icon--success { background: var(--vtm-ok-soft); color: var(--vtm-ok); }
  .tx-kpi__icon--warning { background: var(--vtm-warn-soft); color: var(--vtm-warn); }
  .tx-kpi__icon--danger { background: var(--vtm-crit-soft); color: var(--vtm-crit); }

  .tx-kpi__body { display: flex; flex-direction: column; min-width: 0; }
  .tx-kpi__label { font-size: 13px; color: $muted; }
  .tx-kpi__value { font-size: 26px; font-weight: 700; color: $ink; line-height: 1.2; }
  .tx-kpi__hint { font-size: 11px; color: var(--vtm-ink-3); }
  .tx-kpi--alert .tx-kpi__hint { color: var(--vtm-warn); }

  /* Controls */
  .tx-controls {
    padding: 10px 16px;
    margin-bottom: 12px;
    background: var(--vtm-surface);
    border: 1px solid $border;
    border-radius: 10px;
  }

  /* 单行控制条：模式(左) · 筛选(中,弹性) · 排序+视图(右) */
  .tx-controls--bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 12px;
  }
  .tx-controls--bar .tx-filterbar {
    flex: 1 1 100%;
    width: 100%;
  }
  .tx-controls--bar .tx-controls__view {
    margin-left: auto;
  }
  .tx-filter--date {
    width: 360px !important;
  }
  .tx-filter--status,
  .tx-filter--paytype {
    width: 160px;
  }

  .tx-controls__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  /* 三个分段等宽：让 flex 容器按等份分配，而非各自按内容自适应
     （否则「需要处理」出现角标后变宽，「退款」却不会跟着变宽，三者不再对齐） */
  .tx-seg {
    display: flex;
  }
  .tx-seg ::v-deep .el-radio-button {
    flex: 1 1 0;
  }
  .tx-seg ::v-deep .el-radio-button__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: 100%;
    box-sizing: border-box;
  }
  .tx-seg__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    margin-left: 4px;
    border-radius: 9px;
    background: var(--vtm-crit);
    color: #fff;
    font-size: 12px;
    line-height: 18px;
  }

  .tx-controls__view { display: flex; align-items: center; gap: 10px; }
  .tx-sort { width: 172px; }

  .tx-viewtoggle {
    display: inline-flex;
    border: 1px solid var(--vtm-line-2);
    border-radius: 6px;
    overflow: hidden;
  }

  .tx-viewtoggle button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 32px;
    border: none;
    background: var(--vtm-surface);
    color: var(--vtm-ink-3);
    cursor: pointer;
    font-size: 16px;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .tx-viewtoggle button + button { border-left: 1px solid var(--vtm-line-2); }
  .tx-viewtoggle button:hover { color: $brand; }
  .tx-viewtoggle button.is-active { background: var(--vtm-mint-soft); color: $brand; }

  .tx-panel { min-height: 220px; }

  /* Card grid */
  .tx-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 14px;
  }

  .tx-card {
    display: flex;
    flex-direction: column;
    background: var(--vtm-surface);
    border: 1px solid $border;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  }

  .tx-card:hover {
    border-color: var(--vtm-mint-dim);
    box-shadow: 0 8px 24px rgba(31, 45, 61, 0.1);
    transform: translateY(-2px);
  }

  .tx-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 16px 8px;
  }

  .tx-card__machine { display: flex; align-items: center; gap: 8px; min-width: 0; }
  .tx-card__machine > i { font-size: 18px; color: $brand; flex-shrink: 0; }
  .tx-card__machine-text { display: flex; flex-direction: column; min-width: 0; }
  .tx-card__machine-name { font-size: 14px; font-weight: 600; color: $ink; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tx-card__machine-no { font-size: 11px; color: $muted; }
  .tx-card__flag { flex-shrink: 0; }
  .tx-card__flag i { margin-right: 2px; }

  .tx-card__products { padding: 6px 16px 12px; flex: 1; }
  .tx-card__products-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 12px;
    font-weight: 500;
    color: $muted;
  }
  .tx-card__products-count {
    padding: 1px 9px;
    border-radius: 10px;
    background: var(--vtm-well);
    font-weight: 600;
    color: var(--vtm-ink-2);
  }

  .tx-products { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 4px; }
  .tx-product { width: 84px; flex-shrink: 0; text-align: center; }
  .tx-product__thumb {
    position: relative;
    width: 84px;
    height: 84px;
    border-radius: 8px;
    overflow: hidden;
    background: var(--vtm-well);
    border: 1px solid $border;
  }
  .tx-product--mismatch .tx-product__thumb { border-color: var(--vtm-crit); }
  .tx-product__thumb ::v-deep .el-image { width: 100%; height: 100%; }
  .tx-product__thumb ::v-deep .el-image__inner { object-fit: contain; background: var(--vtm-surface); }
  .tx-product__thumb-ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 24px;
  }
  .tx-product__qty {
    position: absolute;
    right: 4px;
    bottom: 4px;
    padding: 1px 7px;
    border-radius: 9px;
    background: rgba(48, 49, 51, 0.82);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }
  .tx-product__name {
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.3;
    color: var(--vtm-ink-2);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .tx-product__flag { margin-top: 2px; font-size: 11px; color: var(--vtm-crit); }

  .tx-card__foot {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid var(--vtm-line);
    background: var(--vtm-surface-2);
  }
  .tx-card__amount { display: flex; flex-direction: column; }
  .tx-card__amount-label { font-size: 11px; color: $muted; }
  .tx-card__amount strong { font-size: 18px; font-weight: 700; color: var(--vtm-crit); }
  .tx-card__status { display: flex; flex-direction: column; gap: 4px; margin-left: auto; align-items: flex-end; }
  .tx-card__review-btn { flex-shrink: 0; }
  .tx-card__time { padding: 8px 16px; border-top: 1px solid var(--vtm-line); font-size: 11px; color: var(--vtm-ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tx-card__time i { margin-right: 3px; }

  /* Shared list (both modes) */
  .tx-list { background: var(--vtm-surface); border: 1px solid $border; border-radius: 10px; overflow: hidden; }

  .tx-list__head,
  .tx-row {
    display: grid;
    grid-template-columns: 1.5fr 1.4fr 1.8fr 1.1fr 0.8fr 96px;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
  }

  .tx-list--review .tx-list__head,
  .tx-list--review .tx-row {
    grid-template-columns: 1.4fr 1.8fr 1.8fr 0.8fr 110px;
  }

  .tx-list__head {
    background: var(--vtm-surface-2);
    border-bottom: 1px solid $border;
    font-size: 12px;
    font-weight: 600;
    color: $muted;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .tx-row { border-bottom: 1px solid var(--vtm-line); cursor: pointer; transition: background 0.15s ease; }
  .tx-row:last-child { border-bottom: none; }
  .tx-row:hover { background: var(--vtm-mint-soft); }

  .tx-list__num { text-align: right; justify-self: end; }

  .tx-row__machine { display: flex; align-items: center; gap: 8px; min-width: 0; }
  .tx-row__machine > i { font-size: 17px; color: $brand; flex-shrink: 0; }
  .tx-row__machine-text { display: flex; flex-direction: column; min-width: 0; }
  .tx-row__order { display: flex; flex-direction: column; min-width: 0; }
  .tx-row__strong { font-size: 13px; font-weight: 600; color: $ink; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tx-row__mono { font-family: "SFMono-Regular", Consolas, monospace; font-size: 12px; color: var(--vtm-ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tx-row__sub { font-size: 11px; color: $muted; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .tx-row__products { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .tx-thumbs { display: flex; align-items: center; }
  .tx-thumbs__img {
    width: 34px;
    height: 34px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--vtm-well);
    border: 1px solid #fff;
    box-shadow: 0 0 0 1px $border;
    margin-left: -6px;
  }
  .tx-thumbs__img:first-child { margin-left: 0; }
  .tx-thumbs__img ::v-deep .el-image { width: 100%; height: 100%; }
  .tx-thumbs__img ::v-deep .el-image__inner { object-fit: contain; background: var(--vtm-surface); }
  .tx-thumbs__img .tx-product__thumb-ph { font-size: 14px; }
  .tx-thumbs__more { margin-left: 6px; font-size: 12px; font-weight: 600; color: var(--vtm-ink-3); }

  .tx-row__status { display: flex; flex-wrap: wrap; gap: 4px; }
  .tx-row__amount { font-size: 14px; font-weight: 700; color: var(--vtm-crit); }
  .tx-row__action { display: flex; justify-content: flex-end; align-items: center; }
  .tx-row__chevron { color: var(--vtm-ink-3); font-size: 16px; }

  .tx-row__issue { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .tx-issue-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    align-self: flex-start;
    padding: 2px 9px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }
  .tx-issue-chip.is-danger { background: var(--vtm-crit-soft); color: var(--vtm-crit); }
  .tx-issue-chip.is-warning { background: var(--vtm-warn-soft); color: var(--vtm-warn); }

  /* Review cards */
  .tx-review-list { display: grid; gap: 14px; }
  .review-card {
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    gap: 18px;
    padding: 16px;
    background: var(--vtm-surface);
    border: 1px solid $border;
    border-radius: 10px;
  }
  .review-card__media-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 500;
    color: $muted;
  }
  .review-card__videos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; min-height: 90px; }
  .review-card__video {
    position: relative;
    height: 82px;
    border-radius: 6px;
    overflow: hidden;
    background: #000;
    cursor: pointer;
    border: 1px solid $border;
  }
  .review-card__video video { width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
  .review-card__video-play {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    color: #fff;
    background: linear-gradient(180deg, rgba(14, 18, 26, 0.1), rgba(14, 18, 26, 0.32));
  }

  .review-card__body { min-width: 0; display: flex; flex-direction: column; gap: 12px; }
  .review-issue {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
  }
  .review-issue i { font-size: 18px; margin-top: 1px; }
  .review-issue__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .review-issue__text strong { font-size: 14px; }
  .review-issue__text span { color: var(--vtm-ink-2); word-break: break-word; }
  .review-issue.is-danger { background: var(--vtm-crit-soft); color: var(--vtm-crit); }
  .review-issue.is-warning { background: var(--vtm-warn-soft); color: var(--vtm-warn); }

  .review-card__meta { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
  .review-card__meta-item { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .review-card__meta-item span { font-size: 11px; color: $muted; }
  .review-card__meta-item strong { font-size: 13px; color: $ink; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .review-card__products-label { font-size: 11px; color: $muted; }
  .review-card__chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
  .review-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 14px;
    background: var(--vtm-well);
    font-size: 12px;
    color: var(--vtm-ink-2);
  }
  .review-chip__qty { font-weight: 600; color: $brand; }
  .review-card__none { font-size: 12px; color: var(--vtm-ink-3); }
  .review-card__actions { display: flex; gap: 8px; margin-top: auto; }

  .tx-pagination { margin-top: 16px; text-align: right; }

  /* Detail drawer */
  .detail { display: flex; flex-direction: column; height: 100%; }
  .detail__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid $border;
  }
  .detail__topbar-title { display: inline-flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 600; color: $ink; }
  .detail__topbar-title i { color: var(--vtm-ink-3); }
  .detail__scroll { flex: 1; overflow-y: auto; padding: 18px 20px; }

  .detail__hero {
    padding: 16px 18px;
    margin-bottom: 16px;
    border: 1px solid $border;
    border-radius: 10px;
    background: var(--vtm-surface-2);
  }
  .detail__hero-machine { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 600; color: $brand; margin-bottom: 6px; }
  .detail__order { font-family: "SFMono-Regular", Consolas, monospace; font-size: 15px; color: var(--vtm-ink-2); word-break: break-all; }
  .detail__tags { display: flex; gap: 8px; margin: 12px 0 14px; }
  .detail__tags i { margin-right: 3px; }
  .detail__stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .detail__stat { padding: 10px 12px; border-radius: 8px; background: var(--vtm-surface); border: 1px solid $border; }
  .detail__stat span { display: block; font-size: 11px; color: $muted; margin-bottom: 3px; }
  .detail__stat strong { font-size: 16px; color: $ink; }

  .detail__section { padding: 16px 18px; margin-bottom: 14px; border: 1px solid $border; border-radius: 10px; }
  .detail__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--vtm-line);
    font-size: 15px;
    font-weight: 600;
    color: $ink;
  }
  .detail__section-head i { color: var(--vtm-ink-3); margin-right: 6px; }

  .detail__goods { display: grid; gap: 10px; }
  .detail-goods {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 10px;
    border: 1px solid $border;
    border-radius: 8px;
  }
  .detail-goods__thumb { width: 64px; height: 64px; border-radius: 6px; overflow: hidden; background: var(--vtm-well); }
  .detail-goods__thumb ::v-deep .el-image { width: 100%; height: 100%; }
  .detail-goods__thumb ::v-deep .el-image__inner { object-fit: contain; background: var(--vtm-surface); }
  .detail-goods__name { font-size: 14px; font-weight: 600; color: $ink; margin-bottom: 6px; }
  .detail-goods__metrics { display: flex; flex-wrap: wrap; gap: 14px; font-size: 12px; color: $muted; }
  .detail-goods__metrics strong { color: $ink; }
  .detail-goods__metrics .is-mismatch strong { color: var(--vtm-crit); }

  .detail__videos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .detail__video { width: 100%; max-height: 200px; border-radius: 8px; background: #000; }

  .detail__kv-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }
  .detail__kv { display: flex; flex-direction: column; gap: 4px; min-width: 0; padding-bottom: 10px; border-bottom: 1px solid var(--vtm-line); }
  .detail__kv-label { font-size: 12px; color: $muted; }
  .detail__kv-value { font-size: 13px; color: $ink; word-break: break-word; }

  .detail__timeline { display: flex; flex-direction: column; }
  .detail__timeline-node { display: flex; gap: 12px; padding-bottom: 16px; position: relative; }
  .detail__timeline-node:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 12px;
    bottom: 0;
    width: 1px;
    background: var(--vtm-line);
  }
  .detail__timeline-dot { width: 9px; height: 9px; margin-top: 3px; border-radius: 50%; background: $brand; flex-shrink: 0; z-index: 1; }
  .detail__timeline-body { display: flex; flex-direction: column; gap: 2px; }
  .detail__timeline-label { font-size: 12px; color: $muted; }
  .detail__timeline-value { font-size: 13px; color: $ink; }

  .detail__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid $border;
  }

  @media (max-width: 1200px) {
    .tx-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  @media (max-width: 560px) {
    .tx-seg ::v-deep .el-radio-button__inner { padding-left: 10px; padding-right: 10px; }
  }

  @media (max-width: 992px) {
    .tx-list__head { display: none; }
    .tx-list__head,
    .tx-row,
    .tx-list--review .tx-list__head,
    .tx-list--review .tx-row {
      grid-template-columns: 1fr 1fr;
    }
    .tx-row { gap: 8px 12px; }
    .tx-row__products { grid-column: 1 / -1; }
    .review-card { grid-template-columns: 1fr; }
    .review-card__meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
</style>
